import { formReducer } from 'redux-pure-form';
import { uniq } from 'lodash';

import {
  GET_CALENDAR_RESOURCES_BY_ORGS_REQUEST,
  GET_CALENDAR_RESOURCES_BY_ORGS,
  GET_RESOURCE_DATA,
  GET_RESOURCE_DATA_REQUEST,
  GET_COUNTER_DATA,
  GET_RESOURCE_COUNTERS
} from '../actions/resource.js';

import { GET_AGENDA } from '../actions/calendar.js';

import { LOGOUT, RESET_CACHE } from '../actions-types/login-action-types.js';

import { isResourceInstructor } from '../models/resource.js';

const resourceReducer = formReducer('resource');
const resourcesCountersReducer = formReducer('resourcesCounters');
const initResourceReducer = { resources: {} };

export default function ResourceReducer(_state = initResourceReducer, action) {
  let state = _state;

  if (action && action.payload && action.payload.data && action.payload.data.resources) {
    state = {
      ..._state,
      resources: {
        ..._state.resources,
        ...action.payload.data.resources
      }
    };
  }

  switch (action.type) {
    case GET_CALENDAR_RESOURCES_BY_ORGS_REQUEST:
    case GET_RESOURCE_DATA_REQUEST:
      return {
        ...state,
        isProgress: true
      };
    case GET_CALENDAR_RESOURCES_BY_ORGS:
      return {
        ...state,
        isProgress: false,
        calendarResourcesList: action.payload.data.resourcesList
      };
    case GET_RESOURCE_DATA:
      const id = action.payload.resource.id;
      return {
        ..._state,
        resources: {
          ..._state.resources,
          [id]: {
            ..._state.resources[id],
            ...action.payload.resource
          }
        }
      };
    case GET_COUNTER_DATA:
    case GET_RESOURCE_COUNTERS:
      const aircraftID = action.meta.id;
      return {
        ..._state,
        resources: {
          ..._state.resources,
          [aircraftID]: {
            ..._state.resources[aircraftID],
            counters: action.payload.countersList.map(
              resourceID => action.payload.counters[resourceID]
            )
          }
        }
      };
    case LOGOUT:
    case RESET_CACHE:
      return initResourceReducer;
    default:
      return resourcesCountersReducer(resourceReducer(state, action), action);
  }
}

/**
 * Select from resources list only with specific type and location
 * @param  {object} state              redux state
 * @param  {array} resources           resoruces list for filtering
 * @param  {string} type               which type search
 * @param  {array|string} locationsIDs location
 * @return {array}                     filtered resources list
 */
export function selectResourcesByTypeLoc(state, resources, type, locationsIDs) {
  if (Array.isArray(resources)) {
    let resourcesList = resources;
    if (locationsIDs) {
      const locs = [].concat(locationsIDs).reduce((acc, locID) => ({ ...acc, [locID]: true }), {});
      resourcesList = resources.filter(
        resourceID => locs[state.ResourceReducer.resources[resourceID].locationID]
      );
    }
    if (type) {
      const notEqual = type.startsWith('!');
      const typeNotEqual = type.slice(1);
      resourcesList = resourcesList.filter(resourceID => {
        if (notEqual) {
          return state.ResourceReducer.resources[resourceID].type !== typeNotEqual;
        }
        return state.ResourceReducer.resources[resourceID].type === type;
      });
    }
    return resourcesList;
  }
  return [];
}

/**
 * select resoruces by event type several functions
 * @param  {object}  state          redux state
 * @param  {array}   resourcesList  resources lists for filtering
 * @param  {string}  type           event type (regular/maintenance)
 * @param  {array}   filters        filtering functions
 * @return {array}                  filtered resources list
 */
export function selelectResourcesByEventType(state, resourcesList, type, ...filters) {
  if (Array.isArray(resourcesList)) {
    if (type === 'maintenance') {
      return resourcesList.slice(0, 1);
    }
    return resourcesList.filter(resourceID =>
      filters.every(filter => filter(state.ResourceReducer.resources[resourceID]))
    );
  }
  return [];
}

/**
 * select resoruces by several functions
 * @param  {object} state         redux state
 * @param  {array}  resourcesList resources lists for filtering
 * @param  {array}  filters       filtering functions
 * @return {array}                filtered resources list
 */
export function selectResourcesBy(state, resourcesList, ...filters) {
  if (Array.isArray(resourcesList)) {
    return resourcesList.filter(resourceID =>
      filters.every(filter => filter(state.ResourceReducer.resources[resourceID]))
    );
  }
  return [];
}

export function selectResourcesListByEvent(state, eventID) {
  const event =
    state.CalendarReducer.Calendar &&
    state.CalendarReducer.Calendar.events &&
    state.CalendarReducer.Calendar.events[eventID];
  if (event) {
    return event.resourcesList;
  }
  return [];
}

/**
 * select resoruces types by several functions
 * @param  {object} resources     resources
 * @param  {array}  resourcesList resources lists
 * @param  {array}  currentTypes  list first types in reterned types list
 * @return {array}                types list
 */
export function selectResourcesTypesBy(
  resources,
  resourcesList,
  currentTypes = [],
  withoutType = ''
) {
  if (resourcesList && Array.isArray(resourcesList)) {
    const resourcesType = resourcesList
      .map(resourceID => resources[resourceID].type)
      .filter(type => type !== withoutType)
      .sort();
    const _currentTypes = currentTypes.filter(type => resourcesType.includes(type));
    return uniq(_currentTypes.concat(resourcesType));
  }
  return [];
}

export function optionsResourcesGrouped(types, resources, resourcesList) {
  let result = [];
  types.forEach(type => {
    const resourcesType = resourcesList
      .filter(resourceID => type === resources[resourceID].type)
      .map(resourceID => ({
        value: resourceID,
        label: resources[resourceID].name,
        header: false
      }));
    result = result
      .concat({
        label: type,
        header: true
      })
      .concat(resourcesType);
  });
  return result;
}
